import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Award,
  Download,
  ShieldCheck,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import SEO from "@/components/site/SEO";
import lmsApi from "@/lib/lmsApi";

export default function LMSCertificate() {
  const { enrolmentId } = useParams();
  const navigate = useNavigate();

  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const me = await lmsApi.get("/auth/me");

        if (me.data.require_password_change) {
          navigate("/learn/change-password", {
            replace: true,
          });

          return;
        }

        const response = await lmsApi.get(
          `/certificates/enrolments/${enrolmentId}`
        );

        setCertificate(response.data);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate("/learn/login", {
            replace: true,
          });

          return;
        }

        setError(
          err.response?.data?.detail ||
            "We could not load this certificate."
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [enrolmentId, navigate]);

  const downloadPdf = async () => {
    if (!certificate) {
      return;
    }

    setBusy(true);
    setError("");

    try {
      const response = await lmsApi.get(
        `/certificates/${certificate.id}/pdf`,
        {
          responseType: "blob",
        }
      );

      const url = URL.createObjectURL(response.data);

      const anchor = document.createElement("a");

      anchor.href = url;

      anchor.download = `TRAC-Learn-${certificate.certificate_number}.pdf`;

      document.body.appendChild(anchor);

      anchor.click();

      anchor.remove();

      URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "The certificate PDF could not be generated."
      );
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <section className="min-h-screen bg-slate-50 py-20">
        <div className="container-rail text-center text-slate-600">
          Loading certificate…
        </div>
      </section>
    );
  }

  if (!certificate) {
    return (
      <section className="min-h-screen bg-slate-50 py-20">
        <div className="container-rail">
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-800">
            {error || "Certificate unavailable."}
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO
        title={`Certificate ${certificate.certificate_number} | TRAC-Learn`}
        description="TRAC-Learn completion certificate."
        path={`/learn/certificate/${enrolmentId}`}
      />

      <section className="min-h-screen bg-slate-100 py-8 md:py-14">
        <div className="container-rail">
          <button
            type="button"
            onClick={() => navigate("/learn")}
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-navy"
          >
            <ArrowLeft className="h-4 w-4" />
            My learning
          </button>

          {error && (
            <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="mx-auto mt-8 max-w-5xl overflow-hidden rounded-3xl bg-navy p-4 shadow-xl md:p-8">
            <div className="rounded-2xl bg-white px-6 py-12 text-center md:px-16 md:py-16">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-orange-50 text-safety">
                <Award className="h-8 w-8" />
              </div>

              <p className="mt-6 text-xs font-black uppercase tracking-[0.28em] text-safety">
                TRAC-Learn
              </p>

              <h1 className="mt-3 font-display text-4xl font-bold text-navy md:text-5xl">
                Certificate of Completion
              </h1>

              <p className="mt-8 text-slate-500">
                This certifies that
              </p>

              <p className="mt-2 font-display text-3xl font-bold text-navy md:text-4xl">
                {certificate.learner_name}
              </p>

              <p className="mt-8 text-slate-500">
                has successfully completed
              </p>

              <h2 className="mx-auto mt-3 max-w-3xl font-display text-2xl font-bold text-navy md:text-3xl">
                {certificate.course_title}
              </h2>

              <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-slate-600">
                <span>
                  Version {certificate.version_number}
                </span>

                <span>
                  Completed{" "}
                  {new Date(
                    certificate.completed_at
                  ).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </span>

                {certificate.cpd_minutes ? (
                  <span>
                    CPD{" "}
                    {(
                      certificate.cpd_minutes / 60
                    ).toLocaleString("en-GB")}{" "}
                    hours
                  </span>
                ) : null}
              </div>

              <p className="mt-3 text-sm text-slate-500">
                Organisation: {certificate.organisation_name}
              </p>

              <div className="mx-auto mt-10 max-w-2xl border-t border-slate-200 pt-6 text-xs text-slate-500">
                <div className="flex flex-col justify-between gap-2 sm:flex-row">
                  <span>
                    Certificate {certificate.certificate_number}
                  </span>

                  <span className="inline-flex items-center justify-center gap-1">
                    <ShieldCheck className="h-3.5 w-3.5 text-safety" />
                    Verification code {certificate.verification_code}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mx-auto mt-6 flex max-w-5xl justify-end">
            <button
              type="button"
              onClick={downloadPdf}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-lg bg-navy px-5 py-3 text-sm font-bold text-white disabled:opacity-50"
            >
              <Download className="h-4 w-4" />

              {busy
                ? "Preparing PDF…"
                : "Download PDF certificate"}
            </button>
          </div>
        </div>
      </section>
    </>
  );
}